<script lang="ts">
  import { onMount } from 'svelte';
  import { gsap } from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import type { Snippet } from 'svelte';

  gsap.registerPlugin(ScrollTrigger);

  interface Props {
    label: string;
    subtitle: string;
    anchorId: string;
    theme: 'light' | 'dark';
    description: string;
    children: Snippet;
  }

  let { label, subtitle, anchorId, theme, description, children }: Props = $props();

  let sectionRef: HTMLElement | undefined;
  let labelRef: HTMLDivElement | undefined;
  let contentRef: HTMLDivElement | undefined;

  const isLight = $derived(theme === 'light');
  const bgClass = $derived(isLight ? 'bg-parchment' : 'bg-charcoal');
  const labelColor = $derived(isLight ? 'text-charcoal' : 'text-parchment');
  const subtitleColor = $derived(isLight ? 'text-taupe' : 'text-warm-gold');
  const descColor = $derived(isLight ? 'text-charcoal' : 'text-parchment');

  onMount(() => {
    if (!sectionRef || !labelRef || !contentRef) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        labelRef!,
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );

      gsap.fromTo(
        contentRef!.children,
        { opacity: 0, y: 25 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  });
</script>

<section bind:this={sectionRef} id={anchorId} data-theme={isLight ? undefined : 'dark'} class="{bgClass} py-[8vh] md:py-[12vh] px-[4vw]">
  <div class="max-w-[1400px] mx-auto flex flex-col md:flex-row gap-[4vw]">
    <!-- Label Column - Sticky -->
    <div class="md:w-[30%] shrink-0">
      <div bind:this={labelRef} class="md:sticky md:top-[12vh]" style="opacity: 0">
        <div class="font-display text-[clamp(3rem,6vw,5rem)] {labelColor} tracking-[-0.02em]">{label}</div>
      </div>
    </div>

    <!-- Content Column -->
    <div bind:this={contentRef} class="md:w-[70%]">
      <span class="font-accent uppercase text-[0.7rem] tracking-[0.15em] {subtitleColor} block">{subtitle}</span>
      <p class="font-body text-[1.05rem] leading-[1.65] {descColor} mt-4">{description}</p>
      <div class="mt-6">
        {@render children()}
      </div>
    </div>
  </div>
</section>
